import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import schoolLogo from '../school-brand.jpg';
import * as Styles from './styles';

function MapMobile() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const navigate = useNavigate();

  const [quickEmail, setQuickEmail] = useState('');
  const [quickPassword, setQuickPassword] = useState('');
  const [showQuickLogin, setShowQuickLogin] = useState(false);

  // Forgot Password States
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    try {
      localStorage.removeItem('instructorId');
      localStorage.removeItem('learnerId');
      localStorage.removeItem('userId');
      localStorage.removeItem('adminToken');

      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: quickEmail, password: quickPassword })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('needsPasswordReset', data.needsPasswordReset ? 'true' : 'false');

        if (data.role === 'admin') {
          localStorage.setItem('userRole', 'admin');
          localStorage.setItem('adminToken', data.token || 'mock-admin-token-secure');
          window.location.assign('/admin');
        } else if (data.role === 'instructor') {
          localStorage.setItem('userRole', 'instructor');
          localStorage.setItem('instructorId', data.id || data.instructorId);
          localStorage.setItem('token', data.token || '');
          window.location.assign('/instructor-dash');
        } else {
          localStorage.setItem('userRole', 'learner');
          localStorage.setItem('userId', data.id || data.userId);
          localStorage.setItem('learnerId', data.id || data.userId);
          localStorage.setItem('userName', data.name || "Learner Driver");
          localStorage.setItem('token', data.token || '');
          window.location.assign(`/learner-dash/${data.instructor_id || 5}`);
        }
      } else {
        alert(data.error || "Login failed.");
      }
    } catch (err) {
      alert("Server error.");
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      if (res.ok) {
        setForgotMessage('Password reset link sent! Check your email.');
      } else {
        setForgotMessage(data.error || 'Could not send reset link.');
      }
    } catch (err) {
      setForgotMessage('Server error. Please try again.');
    }
  };

  useEffect(() => {
    if (!mapInstanceRef.current && mapRef.current) {
      const map = L.map(mapRef.current, {
        scrollWheelZoom: true,
        attributionControl: false,
        maxZoom: 13,
        minZoom: 5
      }).setView([54.5, -3.2], 5);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
      mapInstanceRef.current = map;

      fetch('/api/instructor-locations')
        .then(res => res.json())
        .then(data => {
          if (!mapInstanceRef.current) return;
          const mapInstance = mapInstanceRef.current;

          if (Array.isArray(data)) {
            data.forEach(inst => {
              if (inst.latitude && inst.longitude) {
                let transArray = ["Car (Manual)"];
                try {
                  transArray = typeof inst.transmission_type === 'string' 
                    ? JSON.parse(inst.transmission_type) 
                    : (inst.transmission_type || ["Car (Manual)"]);
                } catch (e) {
                  transArray = [inst.transmission_type || "Car (Manual)"];
                }

                const formattedBadges = transArray.map(t => {
                  const lower = String(t).toLowerCase();
                  if (lower.includes("automatic")) return "🚙 Automatic";
                  if (lower.includes("motorbike") || lower.includes("bike")) return "🏍️ Motorbike";
                  return "🚗 Manual";
                }).join(' & ');

                const rawPostcode = (inst.postcode || '').replace(/\s+/g, '').toUpperCase();
                const outwardPostcode = rawPostcode.length > 3 ? rawPostcode.slice(0, rawPostcode.length - 3) : rawPostcode;
                const areaDisplay = inst.town ? `${inst.town}${outwardPostcode ? ` (${outwardPostcode})` : ''}` : (outwardPostcode ? `Area (${outwardPostcode})` : 'UK Coverage');

                const latOffset = ((inst.id * 13) % 10 - 5) * 0.0025; 
                const lngOffset = ((inst.id * 17) % 10 - 5) * 0.0025;
                const generalizedLat = parseFloat(inst.latitude) + latOffset;
                const generalizedLng = parseFloat(inst.longitude) + lngOffset;

                const customPinIcon = L.divIcon({
                  className: 'custom-avatar-pin',
                  html: `
                    <div style="width: 38px; height: 38px; border-radius: 50%; overflow: hidden; border: 2.5px solid #c084fc; background: white; box-shadow: 0 4px 15px rgba(192,132,252,0.6);">
                        <img src="${schoolLogo}" alt="Logo" style="width: 100%; height: 100%; object-fit: cover;" />
                    </div>
                  `,
                  iconSize: [38, 38],
                  iconAnchor: [19, 19]
                });

                L.marker([generalizedLat, generalizedLng], { icon: customPinIcon })
                  .addTo(mapInstance)
                  .bindPopup(`
                    <div style="color: #111827; text-align: center; font-family: sans-serif; padding: 4px;">
                        <strong style="font-size: 12px;">${formattedBadges} Lessons</strong><br />
                        <span style="font-size: 11px; color: #7c3aed; font-weight: bold;">📍 Area: ${areaDisplay}</span><br />
                        <span style="font-size: 10px; color: #64748b;">Verified Local Coverage</span><br />
                        <a href="/contact" style="display: inline-block; margin-top: 6px; background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%); color: #fff; padding: 4px 12px; border-radius: 4px; text-decoration: none; font-size: 10px; font-weight: bold;">Enquire / Book ➔</a>
                    </div>
                  `);
              }
            });
          }
        }).catch(err => console.error(err));
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ ...Styles.containerStyle, padding: '0 0 40px 0', overflowX: 'hidden', background: '#0f172a', color: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      
      {/* --- UNIFORM APP HEADER --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: '#090d16', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={schoolLogo} alt="Logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #7c3aed' }} />
          <Link to="/" style={{ color: '#fff', fontWeight: '800', fontSize: '1.1rem', letterSpacing: '-0.5px', textDecoration: 'none' }}>LessonLane</Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Universal House Icon Home Button */}
          <Link 
            to="/" 
            title="Home"
            style={{ 
              background: 'rgba(255,255,255,0.1)', 
              border: 'none', 
              color: '#fff', 
              padding: '6px 10px', 
              borderRadius: '8px', 
              textDecoration: 'none', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="#FFFFFF"/>
            </svg>
          </Link>

          {/* Profile Login Icon */}
          <button 
            onClick={() => { setShowQuickLogin(!showQuickLogin); setIsForgotMode(false); setForgotMessage(''); }}
            title="Portal Login"
            style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C9.24 2 7 4.24 7 7C7 9.42 8.74 11.43 11 11.9V13.1C7.58 13.54 5 16.48 5 20H19C19 16.48 16.42 13.54 13 13.1V11.9C15.26 11.43 17 9.42 17 7C17 4.24 14.76 2 12 2Z" fill="#FFFFFF"/>
            </svg>
          </button>
        </div>
      </div>

      {/* --- QUICK LOGIN / FORGOT PASSWORD EXPANDABLE BOX --- */}
      {showQuickLogin && (
        <div style={{ padding: '16px 16px 0 16px', maxWidth: '450px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          {!isForgotMode ? (
            <form onSubmit={handleQuickLogin} style={{ background: '#1e293b', padding: '20px', borderRadius: '16px', border: '2px solid #7c3aed', boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', color: '#ffffff', fontWeight: '800' }}>Portal Sign In</h4>
                <button type="button" onClick={() => setShowQuickLogin(false)} style={{ background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
              </div>
              <input 
                type="email" 
                placeholder="Email address" 
                value={quickEmail} 
                onChange={e => setQuickEmail(e.target.value)} 
                required 
                style={{ width: '100%', padding: '11px 12px', marginBottom: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }}
              />
              <input 
                type="password" 
                placeholder="Password" 
                value={quickPassword} 
                onChange={e => setQuickPassword(e.target.value)} 
                required 
                style={{ width: '100%', padding: '11px 12px', marginBottom: '6px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }}
              />
              <div style={{ textAlign: 'right', marginBottom: '12px' }}>
                <button 
                  type="button"
                  onClick={() => { setIsForgotMode(true); setForgotMessage(''); }}
                  style={{ background: 'none', border: 'none', fontSize: '11.5px', color: '#a78bfa', textDecoration: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Forgot password?
                </button>
              </div>
              <button type="submit" style={{ width: '100%', padding: '11px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
                Sign In ➔
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPasswordSubmit} style={{ background: '#1e293b', padding: '20px', borderRadius: '16px', border: '2px solid #7c3aed', boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', color: '#ffffff', fontWeight: '800' }}>Reset Password</h4>
                <button type="button" onClick={() => setShowQuickLogin(false)} style={{ background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
              </div>
              <p style={{ fontSize: '11.5px', color: '#94a3b8', marginBottom: '12px', lineHeight: '1.4' }}>
                Enter your account email and we'll send you a recovery link.
              </p>
              <input 
                type="email" 
                placeholder="Enter your email address" 
                value={forgotEmail} 
                onChange={e => setForgotEmail(e.target.value)} 
                required 
                style={{ width: '100%', padding: '11px 12px', marginBottom: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }}
              />
              {forgotMessage && (
                <div style={{ fontSize: '11.5px', color: forgotMessage.includes('sent') ? '#10b981' : '#f87171', marginBottom: '10px' }}>
                  {forgotMessage}
                </div>
              )}
              <button type="submit" style={{ width: '100%', padding: '11px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>
                Send Reset Link ➔
              </button>
              <div style={{ textAlign: 'center' }}>
                <button 
                  type="button" 
                  onClick={() => setIsForgotMode(false)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* --- MAP CONTAINER WITH SOFTER GLOW BORDER --- */}
      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#fff', margin: '0 0 4px' }}>Instructor Network</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>Tap instructor pins to explore coverage areas & lessons.</p>
        </div>
        <div 
          ref={mapRef} 
          style={{ 
            flex: 1, 
            minHeight: '70vh', 
            width: '100%', 
            borderRadius: '20px', 
            overflow: 'hidden', 
            border: '2px solid #a78bfa', 
            boxShadow: '0 8px 30px rgba(124, 58, 237, 0.2)' 
          }} 
        />
      </div>

    </div>
  );
}

export default MapMobile;